'use client';

import { useState } from 'react';
import Link from 'next/link';
import { Plus, Layout } from 'lucide-react';
import { PageHeader } from '@/shared/components/layout/page-header';
import { AiBriefing } from '@/features/dashboard/components/ai-briefing';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { MonitorPerformanceWidget } from '@/features/dashboard/widgets/monitor/monitor-widget';
import { WidgetGrid } from '@/features/dashboard/components/widget-grid';
import { useDashboardLayout } from '@/features/dashboard/hooks/use-dashboard-layout';
import { WIDGET_ORDER } from '@/features/dashboard/widgets/registry';
import { WidgetPicker } from '@/features/dashboard/components/widget-picker';
import { UsageWidget } from '@/features/billing/components/usage-widget';
import { SetupChecklistWidget } from '@/features/onboarding/components/setup-checklist-widget';
import { F082DeadlineBanner } from '@/features/settings/components/f082-deadline-banner';
import { CaenMigrationBanner } from '@/features/settings/components/caen-migration-banner';

function BriefingSlot() {
  const user = useAuthStore((s) => s.user);
  const role = user?.role;
  const isAdmin = role === 'owner' || role === 'admin';
  const enabled = user?.briefingEnabled !== false;
  if (!isAdmin || !enabled) return null;
  return <AiBriefing />;
}

export default function DashboardPage() {
  const [pickerOpen, setPickerOpen] = useState(false);
  const { layout, showWidget, reset: resetLayout } = useDashboardLayout({ visible: WIDGET_ORDER, hidden: [] });
  const visible = layout.visible;
  const hidden = layout.hidden;

  return (
    <div className="space-y-6">
      <PageHeader
        showBack
        title="Dashboard"
        actions={
          <>
            <button
              onClick={() => setPickerOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-3 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
            >
              <Layout className="h-4 w-4" />
              Widget-uri
              {hidden.length > 0 && (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-white text-[0.625rem] font-bold ml-1">
                  {hidden.length}
                </span>
              )}
            </button>
            <Link
              href="/facturi/nou"
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white hover:bg-primary/90 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Factură nouă
            </Link>
          </>
        }
      />

      {/* L57 — Banner Formular 082 ANAF (PFA/II/IF only) */}
      <F082DeadlineBanner />
      <CaenMigrationBanner />

      {/* Onboarding checklist — auto-detect 14 setup items */}
      <SetupChecklistWidget />

      {/* Sumar briefing — doar pentru admin/owner și dacă e activat */}
      <BriefingSlot />

      {/* Monitor Performanță — widget fix full-width */}
      <div className="grid grid-cols-12 gap-4">
        <MonitorPerformanceWidget />
      </div>

      {/* Usage (billing) */}
      <UsageWidget />

      {/* Draggable widgets grid */}
      <WidgetGrid />

      {/* Widget picker modal */}
      <WidgetPicker
        open={pickerOpen}
        visible={visible}
        onClose={() => setPickerOpen(false)}
        onAdd={(id) => showWidget(id)}
        onReset={() => {
          if (confirm('Resetezi dashboard-ul la layout-ul implicit?')) {
            resetLayout();
            setPickerOpen(false);
          }
        }}
      />

    </div>
  );
}
